import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useT, useLp } from "../lib/i18n";
import {
  shouldShowLaunchBanner,
  LAUNCH_DISMISS_KEY,
} from "../lib/launch";

/**
 * Top-of-page strip shown during the Product Hunt launch window. Renders on
 * `/launch` always, and on any other page when `?ref=ph` is in the URL — so
 * social traffic from PH-day posts lands on the home with the discount in
 * view, while warm traffic that lands directly on `/launch` still sees it.
 *
 * Dismissal persists in localStorage so a user who closes the strip on the
 * home doesn't see it re-appear on every navigation. The `/launch` page
 * itself ignores the dismiss flag — that page is *about* the launch.
 */
export function PHBanner() {
  const t = useT();
  const lp = useLp();
  const { pathname, search } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setDismissed(window.localStorage.getItem(LAUNCH_DISMISS_KEY) === "1");
  }, []);

  // Window check first — outside the launch range, render nothing regardless
  // of pathname / query / dismiss state. Cheap to evaluate (Date comparison).
  if (!shouldShowLaunchBanner({ pathname, search })) return null;

  // Honor dismiss only off the dedicated /launch page. The launch page is
  // entirely about the discount; dismissing the strip there would hide
  // duplicate copy and confuse the user reading the discount card below.
  const onLaunchPage = /\/launch(\/|$)/.test(pathname);
  if (dismissed && !onLaunchPage) return null;

  const onDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(LAUNCH_DISMISS_KEY, "1");
    } catch {
      /* private mode — ignore */
    }
  };

  return (
    <div className="bg-rec text-paper">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-2 text-[13px]">
        <Link
          to={lp + "/launch"}
          className="flex items-center gap-2 font-medium hover:opacity-90"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-paper" aria-hidden="true" />
          <span className="truncate">{t.launch.banner.text}</span>
          <span className="shrink-0 underline underline-offset-2 opacity-90">
            {t.launch.banner.cta}
          </span>
        </Link>
        {!onLaunchPage && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t.launch.banner.dismissAriaLabel}
            className="shrink-0 rounded-full p-1 opacity-80 hover:opacity-100"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            >
              <path d="M3 3l8 8M11 3l-8 8" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
